import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { ArrowLeft, ShoppingCart, Star, Package, Truck, Shield, Clock, Check } from 'lucide-react';
import Footer from './Footer';
import { useCurrency } from '../contexts/CurrencyContext';

// Reusable header component matching Design listing page format
const ProductHeader = ({ navigateToProducts, navigateHome, navigateToCart, cart }) => (
  <header className="bg-white shadow">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
      <div className="flex items-center justify-center relative">
        <Button 
          onClick={navigateToProducts} 
          variant="outline"
          className="absolute left-0"
        >
          ← Back to Shop
        </Button>
        <button onClick={navigateHome} className="flex items-center">
          <img 
            src="/logo-nav.png" 
            alt="Create 3D Works Logo" 
            className="h-10 sm:h-12 w-auto object-contain"
            style={{ minWidth: '120px' }}
          />
        </button>
        <button
          onClick={navigateToCart}
          className="absolute right-0 flex items-center space-x-2 font-medium text-slate-600 hover:text-[#0007b5] transition-colors"
        >
          <ShoppingCart className="w-5 h-5" />
          {cart && cart.length > 0 && (
            <span className="absolute -top-2 -right-2 bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {cart.length}
            </span>
          )}
          <span className="hidden sm:inline">Cart</span>
        </button>
      </div>
    </div>
  </header>
);

const ProductDetailPage = ({ productId, navigateToProducts, navigateHome, navigateToCart, addToCart, cart }) => {
  const { formatPrice } = useCurrency();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    loadProduct();
  }, [productId]);

  const loadProduct = async () => {
    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      let apiUrl = backendUrl;
      if (apiUrl.includes('None') || !apiUrl || apiUrl === '') {
        apiUrl = '';
      }

      const response = await fetch(`${apiUrl}/api/products/${productId}`);
      
      if (response.ok) {
        const data = await response.json();
        setProduct(data);
      } else {
        console.error('Failed to load product');
      }
    } catch (error) {
      console.error('Error loading product:', error);
    } finally {
      setLoading(false);
    }
  };

  // formatPrice now comes from useCurrency hook

  const getStockBadge = (quantity) => {
    if (quantity === 0) {
      return <Badge variant="destructive">Out of Stock</Badge>;
    } else if (quantity < 5) {
      return <Badge variant="outline" className="border-brand-orange-500 text-brand-orange-600">Low Stock ({quantity} left)</Badge>;
    } else {
      return <Badge variant="outline" className="border-green-500 text-green-600">In Stock</Badge>;
    }
  };

  const handleAddToCart = () => {
    if (product && addToCart) {
      for (let i = 0; i < quantity; i++) {
        addToCart(product);
      }
      alert(`Added ${quantity}x ${product.name} to cart!`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <ProductHeader 
          navigateToProducts={navigateToProducts}
          navigateHome={navigateHome}
          navigateToCart={navigateToCart}
          cart={cart}
        />
        
        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="animate-pulse grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="w-full h-80 bg-slate-200 rounded-lg"></div>
              <div className="grid grid-cols-4 gap-2">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="h-20 bg-slate-200 rounded-lg"></div>
                ))}
              </div>
            </div>
            <div className="space-y-4">
              <div className="h-8 bg-slate-200 rounded"></div>
              <div className="h-4 bg-slate-200 rounded w-3/4"></div>
              <div className="h-6 bg-slate-200 rounded w-1/2"></div>
              <div className="h-32 bg-slate-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <ProductHeader 
          navigateToProducts={navigateToProducts}
          navigateHome={navigateHome}
          navigateToCart={navigateToCart}
          cart={cart}
        />
        
        <div className="max-w-4xl mx-auto px-4 py-12 text-center">
          <Package className="w-16 h-16 text-slate-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-600 mb-4">Product Not Found</h1>
          <p className="text-slate-500 mb-6">The product you're looking for doesn't exist or may have been removed.</p>
          <Button onClick={navigateToProducts} className="bg-gradient-to-r from-brand-orange-500 to-brand-orange-600 hover:from-brand-orange-600 hover:to-brand-orange-700">
            Return to Shop
          </Button>
        </div>
      </div>
    );
  }

  // Prepare image array (main image + additional images)
  const allImages = product.image_url 
    ? [product.image_url, ...(product.images || [])]
    : (product.images && product.images.length > 0 ? product.images : []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <ProductHeader 
        navigateToProducts={navigateToProducts}
        navigateHome={navigateHome}
        navigateToCart={navigateToCart}
        cart={cart}
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Product Detail Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Product Images */}
          <div className="space-y-4">
            {/* Main Image */}
            <div className="relative bg-white rounded-xl border border-slate-200 overflow-hidden">
              {allImages.length > 0 ? (
                <img
                  src={allImages[selectedImage]}
                  alt={product.name}
                  className="w-full h-80 object-cover"
                />
              ) : (
                <div className="w-full h-80 bg-gradient-to-br from-slate-100 to-slate-200 flex flex-col items-center justify-center">
                  <Package className="w-24 h-24 text-slate-400 mb-4" />
                  <p className="text-slate-500 font-medium">No Image Available</p>
                </div>
              )}
              <div className="absolute top-4 right-4">
                {getStockBadge(product.stock_quantity)}
              </div>
            </div>
            
            {/* Image Thumbnails */}
            {allImages.length > 1 && (
              <div className="grid grid-cols-4 gap-2">
                {allImages.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`relative border-2 rounded-lg overflow-hidden ${
                      selectedImage === index 
                        ? 'border-brand-orange-500' 
                        : 'border-slate-200 hover:border-brand-orange-300'
                    }`}
                  >
                    <img src={image} alt="" className="w-full h-20 object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Information */}
          <div className="space-y-6">
            {/* Product Header */}
            <div>
              <div className="flex items-center space-x-3 mb-3">
                <Badge variant="outline" className="text-sm">{product.manufacturer}</Badge>
                <Badge variant="outline" className="text-sm">{product.category}</Badge>
              </div>
              
              <h1 className="text-3xl font-bold text-slate-800 mb-3">{product.name}</h1>
              
              {product.rating && (
                <div className="flex items-center space-x-2 mb-4">
                  <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-4 h-4 ${
                          i < Math.floor(product.rating)
                            ? 'fill-yellow-400 text-yellow-400'
                            : 'text-slate-300'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-sm font-medium">{product.rating}</span>
                  <span className="text-sm text-slate-600">({product.review_count} reviews)</span>
                </div>
              )}

              <p className="text-lg text-slate-600 leading-relaxed">{product.description}</p>
            </div>

            {/* Pricing */}
            <div className="bg-slate-50 rounded-xl p-4">
              <div className="text-3xl font-bold text-slate-800 mb-2">{formatPrice(product.price)}</div>
              <div className="text-sm text-slate-600">
                SKU: {product.sku}
              </div>
            </div>

            {/* Quantity Selector and Add to Cart */}
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center space-x-4 mb-4">
                <label className="text-sm font-medium text-slate-700">Quantity:</label>
                <div className="flex items-center border border-slate-300 rounded-lg">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="px-3 py-2 hover:bg-slate-100 text-slate-600"
                  >
                    −
                  </button>
                  <Input
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-16 text-center border-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    min="1"
                    max={product.stock_quantity}
                  />
                  <button
                    onClick={() => setQuantity(Math.min(product.stock_quantity, quantity + 1))}
                    className="px-3 py-2 hover:bg-slate-100 text-slate-600"
                  >
                    +
                  </button>
                </div>
              </div>
              
              <Button
                onClick={handleAddToCart}
                disabled={product.stock_quantity === 0}
                className={`w-full ${
                  product.stock_quantity === 0
                    ? 'bg-slate-300 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white'
                } py-3 text-lg`}
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                {product.stock_quantity === 0 ? 'Out of Stock' : `Add to Cart - ${formatPrice(product.price * quantity)}`}
              </Button>
            </div>

            {/* Product Features */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-slate-800">Product Features</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center space-x-3">
                  <Shield className="w-5 h-5 text-green-600" />
                  <span className="text-slate-600">Warranty Included</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Truck className="w-5 h-5 text-blue-600" />
                  <span className="text-slate-600">Fast Shipping</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Check className="w-5 h-5 text-green-600" />
                  <span className="text-slate-600">Quality Guaranteed</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Clock className="w-5 h-5 text-purple-600" />
                  <span className="text-slate-600">24/7 Support</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Detailed Specifications */}
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Package className="w-5 h-5 text-brand-orange-600" />
                <span>Technical Specifications</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {product.specifications && (
                <div className="space-y-3">
                  {product.specifications.build_volume && (
                    <div className="flex justify-between">
                      <span className="font-medium text-slate-700">Build Volume:</span>
                      <span className="text-slate-600">{product.specifications.build_volume}</span>
                    </div>
                  )}
                  {product.specifications.layer_height && (
                    <div className="flex justify-between">
                      <span className="font-medium text-slate-700">Layer Height:</span>
                      <span className="text-slate-600">{product.specifications.layer_height}</span>
                    </div>
                  )}
                  {product.specifications.print_speed && (
                    <div className="flex justify-between">
                      <span className="font-medium text-slate-700">Print Speed:</span>
                      <span className="text-slate-600">{product.specifications.print_speed}</span>
                    </div>
                  )}
                  {product.specifications.connectivity && (
                    <div className="flex justify-between">
                      <span className="font-medium text-slate-700">Connectivity:</span>
                      <span className="text-slate-600">{product.specifications.connectivity}</span>
                    </div>
                  )}
                  {product.specifications.material_compatibility && (
                    <div className="flex justify-between">
                      <span className="font-medium text-slate-700">Material:</span>
                      <span className="text-slate-600">{product.specifications.material_compatibility}</span>
                    </div>
                  )}
                  {product.specifications.diameter && (
                    <div className="flex justify-between">
                      <span className="font-medium text-slate-700">Diameter:</span>
                      <span className="text-slate-600">{product.specifications.diameter}</span>
                    </div>
                  )}
                  {product.specifications.weight && (
                    <div className="flex justify-between">
                      <span className="font-medium text-slate-700">Weight:</span>
                      <span className="text-slate-600">{product.specifications.weight}</span>
                    </div>
                  )}
                  {product.specifications.color && (
                    <div className="flex justify-between">
                      <span className="font-medium text-slate-700">Available Colors:</span>
                      <span className="text-slate-600">{product.specifications.color}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Product Tags */}
              {product.tags && product.tags.length > 0 && (
                <div className="mt-6">
                  <h4 className="font-medium text-slate-700 mb-2">Features:</h4>
                  <div className="flex flex-wrap gap-2">
                    {product.tags.map(tag => (
                      <Badge key={tag} variant="outline" className="capitalize">
                        {tag.replace('-', ' ')}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Star className="w-5 h-5 text-yellow-500" />
                <span>Customer Reviews</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {product.review_count > 0 ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="flex items-center">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`w-4 h-4 ${
                              i < Math.floor(product.rating)
                                ? 'fill-yellow-400 text-yellow-400'
                                : 'text-slate-300'
                            }`}
                          />
                        ))}
                      </div>
                      <span className="font-semibold text-lg">{product.rating}</span>
                    </div>
                    <span className="text-slate-600">{product.review_count} reviews</span>
                  </div>
                  
                  {/* Sample reviews */}
                  <div className="space-y-4">
                    <div className="border-l-4 border-brand-orange-500 pl-4">
                      <div className="flex items-center space-x-2 mb-1">
                        <div className="flex items-center">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                          ))}
                        </div>
                        <span className="text-sm font-medium">Verified Purchase</span>
                      </div>
                      <p className="text-sm text-slate-600 mb-1">
                        "Excellent build quality and easy setup. {product.product_type === 'FDM Printer' ? 'Print quality exceeded expectations.' : 'Great value for the price.'}"
                      </p>
                      <span className="text-xs text-slate-500">- Customer Review</span>
                    </div>

                    <div className="border-l-4 border-brand-orange-500 pl-4">
                      <div className="flex items-center space-x-2 mb-1">
                        <div className="flex items-center">
                          {[...Array(4)].map((_, i) => (
                            <Star key={i} className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                          ))}
                          <Star className="w-3 h-3 text-slate-300" />
                        </div>
                        <span className="text-sm font-medium">Verified Purchase</span>
                      </div>
                      <p className="text-sm text-slate-600 mb-1">
                        "Great product overall. {product.product_type === 'Filament' ? 'Consistent quality and good color.' : 'Setup was straightforward and support was helpful.'}"
                      </p>
                      <span className="text-xs text-slate-500">- Customer Review</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Star className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">No reviews yet</p>
                  <p className="text-sm text-slate-500">Be the first to review this product</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Additional Information */}
        <div className="mt-12">
          <Card>
            <CardHeader>
              <CardTitle>Product Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose prose-slate max-w-none">
                {product.product_type === 'FDM Printer' && (
                  <div>
                    <h3>About This 3D Printer</h3>
                    <p>
                      The {product.name} from {product.manufacturer} is designed for 
                      {product.category.includes('Professional') ? ' professional users who demand precision and reliability' : 
                       product.category.includes('Entry') ? ' beginners and hobbyists looking to start their 3D printing journey' :
                       ' enthusiasts who want performance and versatility'}.
                    </p>
                    
                    <h4>Key Benefits:</h4>
                    <ul>
                      <li>Consistent, high-quality prints</li>
                      <li>User-friendly operation and maintenance</li>
                      <li>Wide material compatibility</li>
                      <li>Reliable performance for continuous use</li>
                    </ul>

                    <h4>Ideal For:</h4>
                    <ul>
                      <li>Prototyping and product development</li>
                      <li>Educational projects and learning</li>
                      <li>Small batch production</li>
                      <li>Creative and artistic applications</li>
                    </ul>
                  </div>
                )}

                {product.product_type === 'Resin Printer' && (
                  <div>
                    <h3>About This Resin 3D Printer</h3>
                    <p>
                      The {product.name} uses advanced MSLA technology to deliver incredibly detailed prints with smooth surface finishes.
                      Perfect for miniatures, jewelry, dental applications, and any project requiring fine detail.
                    </p>
                    
                    <h4>Resin Printing Advantages:</h4>
                    <ul>
                      <li>Exceptional detail resolution</li>
                      <li>Smooth surface finish</li>
                      <li>No visible layer lines</li>
                      <li>Perfect for small, intricate models</li>
                    </ul>
                  </div>
                )}

                {product.product_type === 'Filament' && (
                  <div>
                    <h3>About This Filament</h3>
                    <p>
                      Premium {product.specifications?.material_compatibility} filament from {product.manufacturer}, 
                      engineered for consistent quality and reliable printing performance.
                    </p>
                    
                    <h4>Quality Features:</h4>
                    <ul>
                      <li>Precise diameter tolerance</li>
                      <li>Consistent color throughout</li>
                      <li>Optimal flow characteristics</li>
                      <li>Vacuum-sealed packaging</li>
                    </ul>
                  </div>
                )}

                <h4>Shipping & Returns</h4>
                <p>
                  Free shipping on orders over $50. Standard delivery within 3-5 business days. 
                  30-day return policy on unused items in original packaging.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Shopping Cart Summary - Floating */}
      {cart && cart.length > 0 && (
        <div 
          className="fixed bottom-6 right-6 bg-white border border-slate-200 rounded-xl shadow-lg p-4 max-w-sm cursor-pointer hover:shadow-xl transition-shadow"
          onClick={navigateToCart}
        >
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-slate-800">Shopping Cart</h4>
            <Badge variant="outline">{cart.length} items</Badge>
          </div>
          
          <div className="space-y-2 mb-4 max-h-32 overflow-y-auto">
            {cart.map(item => (
              <div key={item.id} className="flex justify-between text-sm">
                <span className="truncate flex-1">{item.name}</span>
                <span className="font-medium ml-2">{item.quantity}x</span>
              </div>
            ))}
          </div>
          
          <div className="flex items-center justify-between mb-3">
            <span className="font-semibold">Total:</span>
            <span className="font-bold text-lg">
              {formatPrice(cart.reduce((total, item) => total + (item.price * item.quantity), 0))}
            </span>
          </div>
          
          <Button 
            className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white"
            onClick={(e) => {
              e.stopPropagation(); // Prevent card click
              navigateToCart();
            }}
          >
            Checkout ({cart.length})
          </Button>
        </div>
      )}
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ProductDetailPage;